import React, { useRef } from 'react';
import { Box, Flex, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { FiAlertCircle, FiExternalLink, FiUploadCloud } from 'react-icons/fi';
import { KycStage } from '../../api/kyc/types';
import Button from '../base/Button';
import { useTranslation } from '../../hooks/useTranslation';
import KycStatusBadge from './KycStatusBadge';
import { getKycIcon, isKycDocumentType, KYC_UPLOAD_ACCEPT } from './kycMeta';

interface Props {
    stage: KycStage;
    isUploading: boolean;
    uploadDisabled: boolean;
    onFileSelect: (file: File) => void;
}

const KycItemCard: React.FC<Props> = ({ stage, isUploading, uploadDisabled, onFileSelect }) => {
    const { t } = useTranslation();
    const input = useRef<HTMLInputElement>(null);
    const labels = t.kyc.stages[stage.type as keyof typeof t.kyc.stages] || t.kyc.stages.other;
    const canUpload =
        stage.can_upload &&
        isKycDocumentType(stage.type) &&
        (stage.status === 'wait_user' || stage.status === 'failed');
    const fileUrl = isKycDocumentType(stage.type) ? stage.file_url : undefined;
    const safeUrl = fileUrl && /^https?:\/\//i.test(fileUrl) ? fileUrl : undefined;
    const isRejected = stage.status === 'failed';
    return (
        <Box
            bg="white"
            p={{ base: 4, md: 5 }}
            w="full"
            borderLeft="3px solid"
            borderLeftColor={stage.status === 'completed' ? 'green.400' : isRejected ? 'red.400' : 'gray.200'}
        >
            <VStack align="stretch" gap={3}>
                <Flex
                    direction={{ base: 'column', md: 'row' }}
                    justify="space-between"
                    align={{ base: 'stretch', md: 'flex-start' }}
                    gap={3}
                >
                    <HStack gap={3} align="flex-start" minW={0}>
                        <Flex
                            align="center"
                            justify="center"
                            boxSize="40px"
                            bg="gray.100"
                            color="gray.600"
                            flexShrink={0}
                        >
                            <Icon as={getKycIcon(stage.type)} boxSize={5} />
                        </Flex>
                        <VStack align="start" gap={1} minW={0}>
                            <Text fontSize="lg" fontWeight="semibold" color="gray.800">
                                {labels.title}
                            </Text>
                            <Text fontSize="sm" color="gray.600">
                                {labels.description}
                            </Text>
                        </VStack>
                    </HStack>
                    <KycStatusBadge
                        status={stage.status}
                        canUpload={canUpload}
                        notRequired={stage.not_required}
                    />
                </Flex>
                {isRejected && (
                    <HStack align="flex-start" gap={2} bg="red.50" color="red.700" p={3}>
                        <Icon as={FiAlertCircle} boxSize={4} flexShrink={0} mt="2px" />
                        <Text fontSize="sm">
                            {isKycDocumentType(stage.type) && stage.rejection_comment
                                ? stage.rejection_comment
                                : t.kyc.rejectedHint}
                        </Text>
                    </HStack>
                )}
                {safeUrl && (
                    <Box>
                        <Button
                            buttonType="secondary"
                            size="sm"
                            onClick={() => window.open(safeUrl, '_blank', 'noopener,noreferrer')}
                        >
                            <Icon as={FiExternalLink} boxSize={4} />
                            {t.kyc.openDocument}
                        </Button>
                    </Box>
                )}
                {canUpload && (
                    <Box>
                        <input
                            ref={input}
                            type="file"
                            accept={KYC_UPLOAD_ACCEPT}
                            aria-label={`${t.kyc.upload}: ${labels.title}`}
                            disabled={uploadDisabled}
                            style={{ display: 'none' }}
                            onChange={(event) => {
                                const file = event.target.files?.[0];
                                event.target.value = '';
                                if (file) onFileSelect(file);
                            }}
                        />
                        <Button
                            buttonType="success"
                            w={{ base: 'full', md: 'auto' }}
                            isLoading={isUploading}
                            disabled={uploadDisabled}
                            onClick={() => input.current?.click()}
                        >
                            <Icon as={FiUploadCloud} boxSize={4} />
                            {isUploading ? t.kyc.uploading : isRejected ? t.kyc.uploadAgain : t.kyc.upload}
                        </Button>
                    </Box>
                )}
            </VStack>
        </Box>
    );
};
export default KycItemCard;
